import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useResizeDetector } from 'react-resize-detector';
import styled from 'styled-components';

import Column from 'components/common/Column';
import Row from 'components/common/Row';
import { getEnvConstant } from 'constants/env';
import { useProductDetailHooks } from './hooks/useProductDetaillHooks';
import ModifyContentsContainer from './components/ModifyContentsContainer';
import AuthButton from 'components/Button/AuthButton';
import ViewContainer from './components/ViewContainer';
import ProductDetailHeader from './components/ProductDetailHeader';
import ProductBottom from './components/ProductBottom';
import ConfirmDialog from 'components/Modal/ConfirmDialog';
import {
  formatDialogText,
  formatModifyText,
  renderDialogDescription,
  renderDialogTitle,
} from 'utils/formatting';
import Loading from 'components/Loading/Loading';
import HackleManager from 'utils/hackle';
import { formatProductStatus } from 'utils/hackle/parameterFormatting';

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  height: 100vh;
  margin: 0 auto;
  background-color: #f7f7f8;
  overflow-y: ${props => (props.$isMobile ? 'auto' : 'hidden')};
`;

const ProductDetailPage = () => {
  const {
    productInfo,
    menuType,
    productParamInput,
    inquiryList,
    modifyValue,
    isOpenConfirmDialog,
    isOpenCloseProductDetail,
    isRequestInspectionLoading,
    setIsOpenCloseProductDetail,
    setIsOpenConfirmDialog,
    setModifyValue,
    setProductParamInput,
    handleOnChangeProductParamInput,
    handleSetProductStatus,
    handleSetMenuType,
    handleUpdateProduct,
    handleGetProductScheduleTerm,
    handleRefetchInquiryTemplate,
    handleRefetchProduct,
  } = useProductDetailHooks();

  const navigate = useNavigate();
  const { width, height, ref } = useResizeDetector();

  const [viewHeight, setViewHeight] = useState<number>(height - 97);

  const isMobile = width ? width < 768 : window.innerWidth < 768;

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = 'scroll';

      return;
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = ''; // 컴포넌트가 언마운트될 때 스크롤 복구
    };
  }, [isMobile]);

  const handleResizeViewPort = (keyboardHeight: { keyboardHeight: string }) => {
    if (window.Android || modifyValue.modifyStatus === 'introduce') {
      return;
    }

    setViewHeight(height - Number(keyboardHeight.keyboardHeight) - 97);
  };

  const handleResizeOnBlur = () => {
    if (window.Android || modifyValue.modifyStatus === 'introduce') {
      return;
    }

    setViewHeight(height - 97);
  };

  useEffect(() => {
    window.handleResizeViewPort = handleResizeViewPort;
    window.handleResizeOnBlur = handleResizeOnBlur;
  }, []);

  useEffect(() => {
    setViewHeight(height - 97);
  }, [height]);

  const renderViewForm = (): JSX.Element => {
    return (
      <ViewContainer
        isMobile={isMobile}
        productParamInput={productParamInput}
        productInfo={productInfo}
        inquiryList={inquiryList}
        menuType={menuType}
        height={height}
        modifyValue={modifyValue}
        setModifyValue={setModifyValue}
        handleSetProductStatus={handleSetProductStatus}
        handleSetMenuType={handleSetMenuType}
        setIsOpenCloseProductDetail={setIsOpenCloseProductDetail}
      />
    );
  };

  const renderModifyForm = (): JSX.Element => {
    return (
      <ModifyContentsContainer
        isMobile={isMobile}
        productParamInput={productParamInput}
        productInfo={productInfo}
        modifyValue={modifyValue}
        menuType={menuType}
        inquiryList={inquiryList}
        height={height}
        setModifyValue={setModifyValue}
        setProductParamInput={setProductParamInput}
        handleOnChangeProductParamInput={handleOnChangeProductParamInput}
        handleRefetchInquiryTemplate={handleRefetchInquiryTemplate}
        handleGetProductScheduleTerm={handleGetProductScheduleTerm}
        handleRefetchProduct={handleRefetchProduct}
      />
    );
  };

  const renderContents = (): JSX.Element => {
    HackleManager.logProductModifyView(
      Number(productInfo.id),
      productInfo.title,
      productInfo.createdAt,
      productInfo.saleTerm?.endedAt,
      formatProductStatus([productInfo.status]),
    );

    if (isMobile) {
      return (
        <Column>
          <Column
            justifyContent="flex-start"
            style={{
              height: `${viewHeight}px`,
              overflow: 'auto',
            }}
          >
            {renderViewForm()}
            {renderModifyForm()}
          </Column>
          <Column
            style={{
              padding: '16px 20px 32px',
              position: 'sticky',
              bottom: 0,
              background: '#fff',
              borderTop: '1px solid #eaebec',
              zIndex: 3,
            }}
          >
            <AuthButton
              isAbled={modifyValue.isModify}
              name="저장"
              onClick={() => {
                HackleManager.logProductModifySaveComplete(
                  Number(productInfo.id),
                  productInfo.title,
                );

                handleUpdateProduct();
              }}
              style={{ borderRadius: '12px' }}
            />
          </Column>
        </Column>
      );
    }

    return (
      <Column>
        <ProductDetailHeader productInfo={productInfo} />
        <Row
          align="flex-start"
          justifyContent="space-between"
          style={{
            width: '100%',
            maxWidth: '1200px',
          }}
        >
          <Row
            align="flex-start"
            style={{
              width: '100%',
              minWidth: '241px',
              maxWidth: '241px',
              height: `${height}px`,
              borderLeft: '1px solid #eaebec',
              borderRight: '1px solid #eaebec',
              backgroundColor: '#fff',
              zIndex: 1,
              overflowY: 'scroll',
            }}
          >
            {renderViewForm()}
          </Row>
          <Column
            justifyContent="space-between"
            align="flex-start"
            style={{
              position: 'sticky',
              top: 0,
              overflowY:
                modifyValue.modifyStatus === 'introduce' ? 'hidden' : 'scroll',
              width: '100%',
              height: `${height - 162}px`,
              backgroundColor: '#fff',
              zIndex: 1,
              borderRight: '1px solid #eaebec',
            }}
          >
            {renderModifyForm()}
          </Column>
        </Row>
        <ProductBottom
          productInfo={productInfo}
          modifyValue={modifyValue}
          handleUpdateProduct={handleUpdateProduct}
          setIsOpenConfirmDialog={setIsOpenConfirmDialog}
        />
      </Column>
    );
  };

  return (
    <Container ref={ref} $isMobile={isMobile}>
      <Helmet>
        <script
          src={`//dapi.kakao.com/v2/maps/sdk.js?appkey=${getEnvConstant(
            'kakaoAppKey',
          )}&autoload=false&libraries=services`}
        />
        <script
          src={`https://maps.googleapis.com/maps/api/js?language=ko&key=${getEnvConstant(
            'googleMapApiKey',
          )}`}
        />
      </Helmet>
      {productInfo && productParamInput ? renderContents() : <Loading />}
      {isRequestInspectionLoading && (
        <Loading style={{ background: 'rgba(0, 0, 0, 0.4)' }} />
      )}
      {isOpenConfirmDialog && (
        <ConfirmDialog
          title={renderDialogTitle(productInfo.status)}
          description={renderDialogDescription(productInfo.status)}
          onClose={() => {
            setIsOpenConfirmDialog(false);
          }}
          onCloseText="아니요"
          onAcceptText={formatDialogText(productInfo.status)}
          onAccept={() => {
            HackleManager.logProductModifyStatusSaveComplete(
              Number(productInfo.id),
              productInfo.title,
              formatProductStatus([productInfo.status]),
              formatModifyText(productInfo.status),
            );

            handleSetProductStatus(productInfo.status);

            setIsOpenConfirmDialog(false);
          }}
        />
      )}
      {isOpenCloseProductDetail && (
        <ConfirmDialog
          title="수정사항을 저장하지 않았어요"
          description="수정사항을 저장하지 않으면 상품에 반영되지 않아요."
          onClose={() => {
            navigate(-1);
            setIsOpenCloseProductDetail(false);
          }}
          onCloseText="괜찮아요"
          onAcceptText="저장하고 나가기"
          onAccept={() => {
            handleUpdateProduct();
            navigate(-1);
            setIsOpenCloseProductDetail(false);
          }}
        />
      )}
    </Container>
  );
};

export default ProductDetailPage;
